<template>
  <div :id="id" ref="editor">
    <textarea ref="textarea" :name="name" :value="value"/>
  </div>
</template>

<script>
import $ from 'jQuery';
import SimpleMDE from 'simplemde';
import { sanitize } from '@/lib/utils.js';
import beforeChange from '../../markdown-editor-before-change.js';

export default {
  props: {
    value: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    spellChecker: {
      type: Boolean,
      default: true
    },
    mode: {
      type: String,
      default: 'full',
      validator: mode => ['full', 'single-line', 'custom'].includes(mode)
    },
    customToolbar: {
      type: Array,
      default: () => ['bold', 'italic']
    },
    markdownGuideLabel: {
      type: String,
      default: 'About Markdown formatting'
    },
    markdownGuideUrl: {
      type: String,
      default: 'https://awardforce.zendesk.com/hc/en-us/articles/207384793'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: 'markdown-editor'
    },
    inActiveTab: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    toolbarOptions() {
      switch (this.mode) {
        case 'full':
          return [
            'bold',
            'italic',
            'heading',
            '|',
            'quote',
            'unordered-list',
            'ordered-list',
            '|',
            'link',
            '|',
            'preview',
            'spell-checker',
            '|',
            'guide'
          ];
        case 'single-line':
          return [
            'bold',
            'italic',
            '|',
            'preview',
            'spell-checker',
            '|',
            'guide'
          ];
        default:
          return this.customToolbar;
      }
    }
  },
  watch: {
    value(value) {
      if (value !== this.simplemde.value()) {
        this.simplemde.value(value || '');
      }
    },
    inActiveTab(value) {
      if (value && this.simplemde) {
        this.simplemde.codemirror.refresh();
      }
    },
    disabled() {
      this.refresh();
    }
  },
  mounted() {
    this.initialise(this.getOptions());
  },
  beforeDestroy() {
    this.destroy();
  },
  methods: {
    getOptions() {
      return {
        element: this.$refs.textarea,
        spellChecker: false,
        status: false,
        toolbar: this.configureToolbar(),
        insertTexts: {
          link: ['[', '](https://)']
        }
      };
    },
    initialise(options) {
      this.simplemde = new SimpleMDE(options);

      this.simplemde.value(this.value || '');

      this.simplemde.options.previewRender = plainText =>
        sanitize(this.simplemde.markdown(plainText));

      this.simplemde.codemirror.on('change', () => {
        this.$emit('input', this.simplemde.value());
      });

      this.simplemde.codemirror.on('beforeChange', beforeChange);

      this.simplemde.codemirror.on('cursorActivity', this.mdeFocus);
      this.simplemde.codemirror.on('focus', this.mdeFocus);

      this.enableTabbing();
      this.makeAccessible();
      this.updateVisibleTextArea();

      this.simplemde.codemirror.options.readOnly = this.disabled;
      this.updateWrapper();
    },
    destroy() {
      this.simplemde.toTextArea();
      this.simplemde = null;
    },
    refresh() {
      if (!this.simplemde) return;

      this.destroy();
      this.initialise(this.getOptions());
    },
    configureToolbar() {
      if (this.disabled) {
        return [];
      }

      return this.toolbarOptions
        .filter(button => {
          if (!this.spellChecker && button === 'spell-checker') {
            return false;
          }
          return true;
        })
        .map(button => (button === 'guide' ? this.guideButton() : button))
        .map(button =>
          button === 'spell-checker' ? this.spellCheckerButton() : button
        );
    },
    guideButton() {
      return {
        name: 'guide',
        action: this.markdownGuideUrl,
        className: 'fa fa-question-circle',
        title: this.markdownGuideLabel
      };
    },
    spellCheckerButton() {
      return {
        name: 'spell-checker',
        action: this.toggleSpellChecker,
        className: 'fa fa-book',
        title: 'Spell checker'
      };
    },
    enableTabbing() {
      this.simplemde.codemirror.options.extraKeys['Tab'] = false;
      this.simplemde.codemirror.options.extraKeys['Shift-Tab'] = false;
    },
    makeAccessible() {
      if (this.simplemde.toolbarElements) {
        Object.keys(this.simplemde.toolbarElements).forEach(key => {
          const element = this.simplemde.toolbarElements[key];
          if (element.tagName === 'A') {
            element.innerHTML = `<span class="sr-only">${element.title}</span>`;
          }
        });
      }
    },
    toggleSpellChecker(editor) {
      const options = editor.options;

      options.spellChecker = !options.spellChecker;

      this.destroy();
      this.initialise(options);

      // Attempt to trigger the spell checker immediately after the dictionary gets loaded
      setTimeout(() => {
        this.simplemde.value(this.simplemde.value());
      }, 500);

      return editor;
    },
    findWrapper() {
      const wrapper = this.$refs.editor.getElementsByClassName(
        'CodeMirror CodeMirror-wrap'
      );

      return wrapper.length ? wrapper[0] : null;
    },
    updateWrapper() {
      const wrapper = this.findWrapper();
      if (wrapper) {
        if (this.disabled) {
          wrapper.setAttribute('disabled', true);
        } else {
          wrapper.removeAttribute('disabled');
        }
      }
    },
    updateVisibleTextArea() {
      $('#' + this.id)
        .find('textarea')
        .attr('aria-label', 'text area');
    },
    mdeFocus() {
      const cursor = this.simplemde.codemirror.getCursor();

      $(this.$refs.textarea).trigger('mdeFocus', [
        this.simplemde.codemirror,
        cursor.line,
        cursor.ch
      ]);
    }
  }
};
</script>
